import { Card, cn } from '@up2stay/up2promo-ui';
import { Camera, Nfc, type LucideIcon } from 'lucide-react';

export interface ProductScanButtonsProps {
  scanQrLabel: string;
  scanNfcLabel: string;
  onScanQr: () => void;
  onScanNfc?: () => void;
  isQrBusy?: boolean;
  isNfcBusy?: boolean;
  disabled?: boolean;
  showNfc?: boolean;
  /** Outline for first scan; dashed when inviting another code. */
  buttonVariant?: 'outline' | 'dashed';
}

function ScanActionCard({
  label,
  icon: Icon,
  onClick,
  disabled,
  busy,
  dashed,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
  dashed: boolean;
}) {
  return (
    <Card
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || busy}
      aria-busy={busy || undefined}
      onClick={() => {
        if (disabled || busy) return;
        onClick();
      }}
      onKeyDown={(e) => {
        if (disabled || busy) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex min-h-0 w-full cursor-pointer flex-row items-center gap-3 px-3.5 py-3 text-left transition-[border-color,background-color,box-shadow,opacity] sm:min-h-[5.25rem] sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:px-6 sm:py-6 sm:text-center',
        dashed
          ? 'border-dashed border-border/80 bg-muted/20 hover:border-border hover:bg-muted/35'
          : 'border-border/70 bg-card/80 hover:border-border hover:bg-muted/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        (disabled || busy) && 'pointer-events-none opacity-55',
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-nested)] bg-muted/70 text-foreground sm:size-12">
        <Icon className="size-4 sm:size-6" aria-hidden />
      </span>
      <span className="text-[14px] font-semibold leading-snug text-foreground sm:text-[15px]">
        {label}
      </span>
    </Card>
  );
}

/**
 * Shared scan actions for product claim and setup wizards.
 * Equal choice-card footprint on mobile + desktop (side-by-side when both).
 */
export function ProductScanButtons({
  scanQrLabel,
  scanNfcLabel,
  onScanQr,
  onScanNfc,
  isQrBusy = false,
  isNfcBusy = false,
  disabled = false,
  showNfc = false,
  buttonVariant = 'outline',
}: ProductScanButtonsProps) {
  const showNfcButton = Boolean(showNfc && onScanNfc);
  const dashed = buttonVariant === 'dashed';

  return (
    <div
      className={cn(
        'grid gap-2.5 sm:gap-4',
        showNfcButton ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
      )}
    >
      <ScanActionCard
        label={scanQrLabel}
        icon={Camera}
        onClick={onScanQr}
        disabled={disabled}
        busy={isQrBusy}
        dashed={dashed}
      />
      {showNfcButton && onScanNfc ? (
        <ScanActionCard
          label={scanNfcLabel}
          icon={Nfc}
          onClick={onScanNfc}
          disabled={disabled}
          busy={isNfcBusy}
          dashed={dashed}
        />
      ) : null}
    </div>
  );
}
